// Capture recap bus — a tiny module-level pub/sub so a confirm modal (or the
// chip path) can tell the Mirror "a record was just written" and have it show a
// brief in-conversation recap line (C1 retention loop: the user SEES the Mirror
// did something real). Display-ready strings only — each emitter resolves its
// own i18n via useLanguage before emitting, so the subscriber just renders.
//
// Why a bus and not a prop callback: the spine's confirm modals
// (CreateEventModal, NutritionLogModal) write to the DB themselves and are
// rendered through useModuleCapture's `modal` seam, which exposes only a no-arg
// onCreated. Threading rich data back through that seam would mean changing the
// hook body (forbidden by the task). A bus decouples emitter from subscriber:
// when the Mirror isn't mounted (e.g. event created from /agenda), emit is a
// harmless no-op (no subscribers).

// Kind MUST follow the spine convention propose_<kind>_log -> kind (enforced by
// spineWiring.test.ts seam 5). "habit" (singular) matches propose_habit_log;
// "workout" matches propose_workout_log.
export type CaptureRecapKind = "nutrition" | "agenda" | "task" | "note" | "focus" | "checkin" | "sleep" | "habit" | "workout" | "journal" | "finance" | "body" | "email" | "file" | "connected_task" | "people_task" | "personal";

export interface CaptureRecap {
  kind: CaptureRecapKind;
  /** Localized one-word headline, e.g. "Refeição registrada". */
  title: string;
  /** Optional data line built from the row just written, e.g. "Almoço · 620 kcal". */
  subtitle?: string;
}

type Listener = (recap: CaptureRecap) => void;

const listeners = new Set<Listener>();

export function subscribeCaptureRecap(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function emitCaptureRecap(recap: CaptureRecap): void {
  // Snapshot to tolerate a listener unsubscribing during iteration.
  for (const fn of Array.from(listeners)) {
    try {
      fn(recap);
    } catch (err) {
      console.warn("[captureRecapBus] listener threw:", err);
    }
  }
}

/** Emoji per kind — language-neutral, kept out of i18n on purpose. */
export const CAPTURE_RECAP_ICON: Record<CaptureRecapKind, string> = {
  nutrition: "🍽️",
  agenda: "🗓️",
  task: "✅",
  note: "📝",
  focus: "🎯",
  checkin: "☀️",
  sleep: "😴",
  habit: "🔁",
  workout: "🏋️",
  journal: "📖",
  finance: "💸",
  body: "📏",
  email: "✉️",
  file: "📄",
  connected_task: "✅",
  people_task: "✅",
  personal: "📋",
};

/** Route per kind — tapping a recap deep-links to the module page that owns the
 *  record just written. Reuses existing app routes (no new pages). */
export const CAPTURE_RECAP_ROUTE: Record<CaptureRecapKind, string> = {
  nutrition: "/nutrition",
  agenda: "/agenda",
  task: "/tasks",
  note: "/notes",
  focus: "/focus",
  checkin: "/check-in",
  sleep: "/sleep",
  habit: "/habits",
  workout: "/fitness",
  // Journal entries live in journal_entries; the Diário page is not routed yet,
  // so the recap deep-links to the closest existing writing surface (Notes).
  journal: "/notes",
  finance: "/finance",
  body: "/fitness/body-scan",
  email: "/settings/connections?group=messages",
  file: "/settings/connections?group=knowledge",
  connected_task: "/settings/connections?group=calendar",
  people_task: "/settings/connections?group=calendar",
  personal: "/actions",
};
